/**
 * Het modelfilter van de Chats-pagina (06-10-2026, Jordy).
 *
 * Een modeltegel is model + platform (één slug, bijvoorbeeld `fc_…`). Een klik
 * kiest dat model op dat platform; nog een klik, of "All", heft het op. De
 * tegel, de keuzelijst "All models" en het platformfilter (All / FanCentro /
 * F2F) lezen en schrijven dezelfde twee waarden, die in de URL staan
 * (`?model=<slug>&platform=<platform>`): blijft staan na verversen en is per
 * gebruiker (ieder zijn eigen adresbalk).
 *
 * Alle panelen filteren op de SERVER (`model_slug` en `platform`), zodat er
 * niets wegvalt dat net buiten de eerste bladzijde ligt. De rechten blijven
 * op de server: een filter kan nooit meer tonen dan iemand mag zien.
 */

export interface ChatsFilter {
  /** De gekozen slug, of '' voor alle modellen. */
  model: string
  /** 'all' | 'fancentro' | 'f2f' */
  platform: string
}

export interface FilterTegel {
  slug: string
  platform: string
}

const PLATFORMS = new Set(['all', 'fancentro', 'f2f'])

/** Lees het filter uit de URL; onbekende waarden worden "alles". */
export function leesFilter(params: URLSearchParams): ChatsFilter {
  const platform = params.get('platform') || 'all'
  return {
    model: (params.get('model') || '').trim(),
    platform: PLATFORMS.has(platform) ? platform : 'all',
  }
}

/** Schrijf het filter terug in de URL; andere parameters blijven staan. */
export function schrijfFilter(params: URLSearchParams, f: ChatsFilter): URLSearchParams {
  const uit = new URLSearchParams(params)
  if (f.model) uit.set('model', f.model)
  else uit.delete('model')
  if (f.platform && f.platform !== 'all') uit.set('platform', f.platform)
  else uit.delete('platform')
  return uit
}

/** Klik op een tegel: kies model + platform, of hef het filter op. */
export function tegelKlik(f: ChatsFilter, t: FilterTegel): ChatsFilter {
  if (f.model === t.slug) return { model: '', platform: f.platform }
  return { model: t.slug, platform: t.platform }
}

/** De keuzelijst "All models": een slug kiest model + platform, '' of 'all' heft op. */
export function modelKies(f: ChatsFilter, slug: string, tegels: FilterTegel[]): ChatsFilter {
  if (!slug || slug === 'all') return { model: '', platform: f.platform }
  const t = tegels.find((x) => x.slug === slug)
  return { model: slug, platform: t ? t.platform : f.platform }
}

/** Het platformfilter: een ander platform dan dat van het gekozen model heft het model op. */
export function platformKies(f: ChatsFilter, platform: string, tegels: FilterTegel[]): ChatsFilter {
  const p = PLATFORMS.has(platform) ? platform : 'all'
  if (!f.model) return { model: '', platform: p }
  const t = tegels.find((x) => x.slug === f.model)
  const modelPlatform = t ? t.platform : f.platform
  return p === modelPlatform ? { model: f.model, platform: p } : { model: '', platform: p }
}

/** De queryparameters voor de server: `&model_slug=…&platform=…`, of ''. */
export function filterQs(f: ChatsFilter): string {
  let qs = ''
  if (f.model) qs += `&model_slug=${encodeURIComponent(f.model)}`
  if (f.platform && f.platform !== 'all') qs += `&platform=${encodeURIComponent(f.platform)}`
  return qs
}
